/**
 * Canonical registry of HL7 instruments that consume `value-mappings` entries
 * via the `hl7:<name>[:<component>]` lookup-key convention. Single source of
 * truth shared across packages — the admin-console authoring UI and the
 * hl7-processor both build on these lookup keys, so neither redefines them.
 *
 * The runtime registry in
 * `apps/hl7-processor/src/handlers/oru/shared/globalObservationRegistry.ts` is keyed
 * by observation code (LOINC/SNOMED/AgileMD-internal) and carries runtime-only
 * dispatch fields (valueMapping, customBuilder); it must reference exactly the
 * `lookupKey`s advertised here.
 * Adding a new instrument means adding it here and wiring the processor's
 * registry to the same `lookupKey` — unless the key is consumed downstream of
 * an extract (e.g. `hl7:hospital-service:*` in the data pipeline), in which
 * case the processor never resolves it and there is nothing to wire.
 *
 * The structure is intentionally flat — a closed list of instruments and
 * their components — rather than data-driven, so admins editing a mapping
 * see a typo-safe picker matching what the processor will actually look up.
 *
 * Each key also carries `guidance`: the result type the processor consumes,
 * what the MATCH side should contain, and (where the vocabulary is closed)
 * the canonical answer list. Answer codes were verified against the LOINC
 * normative answer lists (LL520-8/LL521-6/LL522-4/LL523-2/LL525-7/LL524-0
 * for Braden, LL355-9/LL356-7/LL357-5 for Glasgow, LL1845-8 for NEMSIS,
 * LL2024-9 for NIH stroke scale) and the org terminology server. RASS and
 * Ramsay are numeric because downstream consumers (ehr-ecart AVPU) read the
 * score from `valueQuantity`, not a coding.
 */

import { OBSERVATION_CODE_SYSTEMS } from './Ecart.ts';
import { HL7_ADT_INSTRUMENTS } from './Hl7AdtInstruments.ts';

export const HL7_INSTRUMENT_CODE_SYSTEMS = {
  LOINC: 'http://loinc.org',
  SNOMED: 'http://snomed.info/sct',
  INTERNAL: OBSERVATION_CODE_SYSTEMS.AGILEMD_INTERNAL
} as const;

/**
 * Default fan-out delimiter for clinical-codes expansion: the HL7 repetition
 * separator (`~`). When a row's `expansion.delimiter` is absent or `~`, OBX-5
 * is split on HL7 repetitions; a custom single-char delimiter splits the raw
 * field instead. Shared by the hl7-processor runtime and the admin-console
 * authoring UI so the default can't drift between them.
 */
export const FAN_OUT_DELIMITER_DEFAULT = '~';

/**
 * Canonical QuestionnaireResponse fields a `clinical-codes` row may mirror into
 * an extra item for parity with a legacy FHIR template. Closed on purpose: an
 * arbitrary HL7 path would recreate the template DSL the mapping tables
 * replaced. Shared so the schema, the processor and the console agree.
 */
export const QR_MIRRORED_FIELDS = Object.freeze({
  AUTHORED: 'authored',
  AUTHOR: 'author'
} as const);
export type Hl7InstrumentCodeSystem =
  (typeof HL7_INSTRUMENT_CODE_SYSTEMS)[keyof typeof HL7_INSTRUMENT_CODE_SYSTEMS];

/** The only result types the `hl7:*` instruments consume. */
export type InstrumentResultType = 'string' | 'number' | 'object';

export interface InstrumentAnswer {
  /**
   * Stored as the rule output: a LOINC LA answer code, a numeric score, or a
   * coded-concept object (`{ code, display, system? }`). Object answers match
   * existing rule outputs by the keys the answer defines.
   */
  output: string | number | Record<string, string>;
  /** What the answer means; shown beside the output in pickers. */
  label: string;
  /** Typical raw feed value; prefills the rule MATCH input on "Prefill". */
  defaultInput?: string;
}

export interface InstrumentGuidance {
  /** The only output type the processor consumes for this lookup key. */
  resultType: InstrumentResultType;
  /**
   * Additional result types the runtime also accepts (e.g. disorientation
   * coerces both `"true"` strings and `{ value: "true" }` objects). Entries
   * stored with one of these don't get the type-mismatch warning, but new
   * entries and the answer picker use `resultType`.
   */
  acceptedResultTypes?: InstrumentResultType[];
  /** One-line statement of what this entry maps; shown in the editor. */
  description: string;
  /** What the MATCH side should contain (raw feed value). */
  inputHint: string;
  /**
   * LOINC AnswerList URL stamped as the emitted answer coding's `system` (e.g.
   * `https://r.details.loinc.org/AnswerList/LL520-8.html`) instead of the bare
   * `http://loinc.org`. Mirrors legacy fhir-transform so ehr-ecart's answer-list
   * -filtered selectors resolve the LA-code. Present only for LA-coded answer
   * components whose codings a downstream consumer parses.
   */
  answerList?: string;
  /**
   * Closed output vocabulary. Omitted when outputs are free-form (org-specific
   * object shapes) or when the span is continuous — a total score states its
   * bounds in `scoreRange` instead of enumerating every value.
   */
  answers?: InstrumentAnswer[];
  /**
   * Inclusive bounds of a continuous numeric score, for entries whose outputs
   * are the input itself. `numericScoreSpan` reads it to seed the passthrough
   * range rule that lets an in-range value through; without such a rule the
   * evaluator yields no output and the observation is dropped.
   */
  scoreRange?: { min: number; max: number };
  /** Starter keys for `object` outputs; seeds new rules with this shape. */
  objectTemplate?: Record<string, string>;
}

export interface Hl7InstrumentComponent {
  /** Component slug used in the lookupKey suffix (e.g. `friction`). */
  key: string;
  /** Editor-facing label. */
  label: string;
  /** Leaf-level LOINC for visual reference; not used in lookupKey. */
  leafLoinc?: string;
  guidance: InstrumentGuidance;
}

export interface Hl7Instrument {
  /** Instrument slug used in the lookupKey (e.g. `braden`). */
  name: string;
  label: string;
  /** Panel-level LOINC for visual reference; not used in lookupKey. */
  panelLoinc?: string;
  panelSystem?: Hl7InstrumentCodeSystem;
  /** Null when the instrument has no leaf breakdown (bare-coded). */
  components: Hl7InstrumentComponent[] | null;
  /** Required when `components` is null (the instrument IS the lookup key). */
  guidance?: InstrumentGuidance;
}

const SUBSCORE_INPUT_HINT =
  'Raw OBX-5 value exactly as your feed sends it — typically the bare ' +
  'numeric subscore (e.g. "2"), but some feeds send text like "2 - Chairfast". ' +
  'Add one rule per value your feed produces.';

const TOTAL_SCORE_GUIDANCE = (min: number, max: number): InstrumentGuidance => ({
  resultType: 'number',
  description:
    `Total score (${min}\u2013${max}). Needs a passthrough range rule to send ` +
    'the score through unchanged; a new entry is seeded with one. Add exact ' +
    'rules on top only for non-numeric values your feed also sends.',
  inputHint: `Raw OBX-5 total score (${min}\u2013${max}).`,
  scoreRange: { min, max }
});

export const HL7_INSTRUMENT_REGISTRY: readonly Hl7Instrument[] = [
  {
    name: 'braden',
    label: 'Braden Scale',
    panelLoinc: '38228-3',
    panelSystem: HL7_INSTRUMENT_CODE_SYSTEMS.LOINC,
    components: [
      {
        key: 'total',
        label: 'Total Score',
        leafLoinc: '38227-5',
        guidance: TOTAL_SCORE_GUIDANCE(6, 23)
      },
      {
        key: 'sensory',
        label: 'Sensory Perception',
        leafLoinc: '38222-6',
        guidance: {
          resultType: 'string',
          description: 'Maps the feed value to a LOINC answer code (list LL520-8).',
          answerList: 'https://r.details.loinc.org/AnswerList/LL520-8.html',
          inputHint: SUBSCORE_INPUT_HINT,
          answers: [
            { output: 'LA9603-7', label: 'Completely limited', defaultInput: '1' },
            { output: 'LA9604-5', label: 'Very limited', defaultInput: '2' },
            { output: 'LA9605-2', label: 'Slightly limited', defaultInput: '3' },
            { output: 'LA9606-0', label: 'No impairment', defaultInput: '4' }
          ]
        }
      },
      {
        key: 'moisture',
        label: 'Moisture Exposure',
        leafLoinc: '38229-1',
        guidance: {
          resultType: 'string',
          description: 'Maps the feed value to a LOINC answer code (list LL521-6).',
          answerList: 'https://r.details.loinc.org/AnswerList/LL521-6.html',
          inputHint: SUBSCORE_INPUT_HINT,
          answers: [
            { output: 'LA9607-8', label: 'Constantly moist', defaultInput: '1' },
            { output: 'LA9608-6', label: 'Very moist', defaultInput: '2' },
            { output: 'LA9609-4', label: 'Occasionally moist', defaultInput: '3' },
            { output: 'LA9610-2', label: 'Rarely moist', defaultInput: '4' }
          ]
        }
      },
      {
        key: 'activity',
        label: 'Physical Activity',
        leafLoinc: '38223-4',
        guidance: {
          resultType: 'string',
          description: 'Maps the feed value to a LOINC answer code (list LL522-4).',
          answerList: 'https://r.details.loinc.org/AnswerList/LL522-4.html',
          inputHint: SUBSCORE_INPUT_HINT,
          answers: [
            { output: 'LA6742-6', label: 'Bedfast', defaultInput: '1' },
            { output: 'LA9611-0', label: 'Chairfast', defaultInput: '2' },
            { output: 'LA9612-8', label: 'Walks occasionally', defaultInput: '3' },
            { output: 'LA9613-6', label: 'Walks frequently', defaultInput: '4' }
          ]
        }
      },
      {
        key: 'mobility',
        label: 'Physical Mobility',
        leafLoinc: '38224-2',
        guidance: {
          resultType: 'string',
          description: 'Maps the feed value to a LOINC answer code (list LL523-2).',
          answerList: 'https://r.details.loinc.org/AnswerList/LL523-2.html',
          inputHint: SUBSCORE_INPUT_HINT,
          answers: [
            { output: 'LA9614-4', label: 'Completely immobile', defaultInput: '1' },
            { output: 'LA9604-5', label: 'Very limited', defaultInput: '2' },
            { output: 'LA9605-2', label: 'Slightly limited', defaultInput: '3' },
            { output: 'LA120-8', label: 'No limitation', defaultInput: '4' }
          ]
        }
      },
      {
        key: 'nutrition',
        label: 'Nutrition Intake Pattern',
        leafLoinc: '38225-9',
        guidance: {
          resultType: 'string',
          description: 'Maps the feed value to a LOINC answer code (list LL525-7).',
          answerList: 'https://r.details.loinc.org/AnswerList/LL525-7.html',
          inputHint: SUBSCORE_INPUT_HINT,
          answers: [
            { output: 'LA9615-1', label: 'Very poor', defaultInput: '1' },
            { output: 'LA9616-9', label: 'Probably inadequate', defaultInput: '2' },
            { output: 'LA8913-1', label: 'Adequate', defaultInput: '3' },
            { output: 'LA9206-9', label: 'Excellent', defaultInput: '4' }
          ]
        }
      },
      {
        key: 'friction',
        label: 'Friction & Shear',
        leafLoinc: '38226-7',
        guidance: {
          resultType: 'string',
          description: 'Maps the feed value to a LOINC answer code (list LL524-0).',
          answerList: 'https://r.details.loinc.org/AnswerList/LL524-0.html',
          inputHint: SUBSCORE_INPUT_HINT,
          answers: [
            { output: 'LA9617-7', label: 'Problem', defaultInput: '1' },
            { output: 'LA9618-5', label: 'Potential problem', defaultInput: '2' },
            { output: 'LA9619-3', label: 'No apparent problem', defaultInput: '3' }
          ]
        }
      }
    ]
  },
  {
    name: 'glasgow',
    label: 'Glasgow Coma Scale',
    panelLoinc: '35088-4',
    panelSystem: HL7_INSTRUMENT_CODE_SYSTEMS.LOINC,
    components: [
      {
        key: 'eye',
        label: 'Eye Opening',
        leafLoinc: '9267-6',
        guidance: {
          resultType: 'string',
          description: 'Maps the feed value to a LOINC answer code (list LL355-9).',
          answerList: 'https://r.details.loinc.org/AnswerList/LL355-9.html',
          inputHint: SUBSCORE_INPUT_HINT,
          answers: [
            { output: 'LA6553-7', label: 'No eye opening', defaultInput: '1' },
            { output: 'LA6554-5', label: 'Eye opening to pain', defaultInput: '2' },
            { output: 'LA6555-2', label: 'Eye opening to verbal command', defaultInput: '3' },
            { output: 'LA6556-0', label: 'Eyes open spontaneously', defaultInput: '4' }
          ]
        }
      },
      {
        key: 'motor',
        label: 'Motor Response',
        leafLoinc: '9268-4',
        guidance: {
          resultType: 'string',
          description: 'Maps the feed value to a LOINC answer code (list LL357-5).',
          answerList: 'https://r.details.loinc.org/AnswerList/LL357-5.html',
          inputHint: SUBSCORE_INPUT_HINT,
          answers: [
            { output: 'LA6562-8', label: 'No motor response', defaultInput: '1' },
            { output: 'LA6563-6', label: 'Extension to pain', defaultInput: '2' },
            { output: 'LA6564-4', label: 'Flexion to pain', defaultInput: '3' },
            { output: 'LA6565-1', label: 'Withdrawal from pain', defaultInput: '4' },
            { output: 'LA6566-9', label: 'Localizing pain', defaultInput: '5' },
            { output: 'LA6567-7', label: 'Obeys commands', defaultInput: '6' }
          ]
        }
      },
      {
        key: 'verbal',
        label: 'Verbal Response',
        leafLoinc: '9270-0',
        guidance: {
          resultType: 'string',
          description: 'Maps the feed value to a LOINC answer code (list LL356-7).',
          answerList: 'https://r.details.loinc.org/AnswerList/LL356-7.html',
          inputHint: SUBSCORE_INPUT_HINT,
          answers: [
            {
              output: 'LA6557-8',
              label: 'No verbal response (>2 yrs); no vocal response (<=2 yrs)',
              defaultInput: '1'
            },
            { output: 'LA6558-6', label: 'Incomprehensible sounds', defaultInput: '2' },
            { output: 'LA6559-4', label: 'Inappropriate words', defaultInput: '3' },
            { output: 'LA6560-2', label: 'Confused', defaultInput: '4' },
            { output: 'LA6561-0', label: 'Oriented', defaultInput: '5' }
          ]
        }
      },
      {
        key: 'total',
        label: 'Total Score',
        leafLoinc: '9269-2',
        guidance: TOTAL_SCORE_GUIDANCE(3, 15)
      }
    ]
  },
  {
    name: 'nih-loc',
    label: 'NIH Stroke Scale — Level of Consciousness',
    panelLoinc: '70184-7',
    panelSystem: HL7_INSTRUMENT_CODE_SYSTEMS.LOINC,
    components: null,
    guidance: {
      resultType: 'string',
      description: 'Maps the feed value to a LOINC answer code (list LL2024-9).',
      answerList: 'https://r.details.loinc.org/AnswerList/LL2024-9.html',
      inputHint:
        'Raw OBX-5 value exactly as your feed sends it — typically the 0–3 score. ' +
        'Add one rule per value your feed produces.',
      answers: [
        { output: 'LA18436-8', label: 'Alert, keenly responsive', defaultInput: '0' },
        {
          output: 'LA18437-6',
          label: 'Not alert; but arousable by minor stimulation',
          defaultInput: '1'
        },
        {
          output: 'LA18438-4',
          label: 'Not alert; requires repeated stimulation to attend',
          defaultInput: '2'
        },
        {
          output: 'LA18439-2',
          label: 'Responds only with reflex motor or autonomic effects, or unresponsive',
          defaultInput: '3'
        }
      ]
    }
  },
  {
    name: 'nemsis-lor',
    label: 'NEMSIS Level of Responsiveness',
    panelLoinc: '67775-7',
    panelSystem: HL7_INSTRUMENT_CODE_SYSTEMS.LOINC,
    components: null,
    guidance: {
      resultType: 'string',
      description: 'Maps the feed value to a LOINC answer code (AVPU, list LL1845-8).',
      answerList: 'https://r.details.loinc.org/AnswerList/LL1845-8.html',
      inputHint:
        'Raw OBX-5 value exactly as your feed sends it — e.g. "Alert", "Verbal", ' +
        '"Pain", "Unresponsive". Add one rule per value your feed produces.',
      answers: [
        { output: 'LA9340-6', label: 'Alert', defaultInput: 'Alert' },
        { output: 'LA17108-4', label: 'Verbal', defaultInput: 'Verbal' },
        { output: 'LA17107-6', label: 'Painful', defaultInput: 'Painful' },
        { output: 'LA9343-0', label: 'Unresponsive', defaultInput: 'Unresponsive' }
      ]
    }
  },
  {
    name: 'rass',
    label: 'Richmond Agitation-Sedation Scale',
    panelLoinc: '457441000124102',
    panelSystem: HL7_INSTRUMENT_CODE_SYSTEMS.SNOMED,
    components: null,
    guidance: {
      resultType: 'number',
      description:
        'Maps the feed value to the numeric RASS score (+4 combative … −5 unarousable). ' +
        'Downstream consumers read the score as a number, not a code.',
      inputHint:
        'Raw OBX-5 value exactly as your feed sends it. For numeric feeds, a ' +
        'single passthrough range (−5 to 4) sends the score through unchanged — ' +
        'add exact rules only for text variants ("Drowsy") your feed also sends.',
      answers: [
        { output: 4, label: 'Combative', defaultInput: '4' },
        { output: 3, label: 'Very agitated', defaultInput: '3' },
        { output: 2, label: 'Agitated', defaultInput: '2' },
        { output: 1, label: 'Restless', defaultInput: '1' },
        { output: 0, label: 'Alert and calm', defaultInput: '0' },
        { output: -1, label: 'Drowsy', defaultInput: '-1' },
        { output: -2, label: 'Light sedation', defaultInput: '-2' },
        { output: -3, label: 'Moderate sedation', defaultInput: '-3' },
        { output: -4, label: 'Deep sedation', defaultInput: '-4' },
        { output: -5, label: 'Unarousable', defaultInput: '-5' }
      ]
    }
  },
  {
    name: 'ramsay',
    label: 'Ramsay Sedation Scale',
    panelLoinc: '281400004',
    panelSystem: HL7_INSTRUMENT_CODE_SYSTEMS.SNOMED,
    components: null,
    guidance: {
      resultType: 'number',
      description:
        'Maps the feed value to the numeric Ramsay score (1–6). Downstream ' +
        'consumers read the score as a number, not a code.',
      inputHint:
        'Raw OBX-5 value exactly as your feed sends it — typically the 1–6 score. ' +
        'A single passthrough range (1 to 6) sends the score through unchanged; ' +
        'add exact rules only for non-numeric values your feed produces.',
      answers: [
        { output: 1, label: 'Anxious and agitated or restless or both', defaultInput: '1' },
        { output: 2, label: 'Cooperative, oriented, and tranquil', defaultInput: '2' },
        { output: 3, label: 'Responds to commands only', defaultInput: '3' },
        { output: 4, label: 'Brisk response to light glabellar tap', defaultInput: '4' },
        { output: 5, label: 'Sluggish response to light glabellar tap', defaultInput: '5' },
        { output: 6, label: 'No response to light glabellar tap', defaultInput: '6' }
      ]
    }
  },
  {
    name: 'oxygen-device',
    label: 'Inhaled Oxygen Mechanism',
    panelLoinc: '3152-6',
    panelSystem: HL7_INSTRUMENT_CODE_SYSTEMS.LOINC,
    components: null,
    guidance: {
      resultType: 'object',
      description:
        'Maps the feed device name to a coded concept `{ code, display, system }`. ' +
        'Pick a device from the list, or use Custom for devices not listed.',
      inputHint:
        'Raw OBX-5 device string exactly as your feed sends it (e.g. "NC", ' +
        '"Nasal cannula", "HHFNC"). Add one rule per device string your feed produces.',
      // The LL2746-2 vocabulary below is pinned by hl7-processor's unit + e2e
      // tests (oru-value-mappings.test.ts); the list is not in official LOINC
      // and ehr-ecart cannot currently read these codings — flagged for a
      // contract fix. Until then this is the convention the processor emits.
      answers: [
        {
          output: {
            code: 'LA10086-2',
            display: 'Nasal cannula',
            system: 'https://r.details.loinc.org/AnswerList/LL2746-2.html'
          },
          label: 'Nasal cannula',
          defaultInput: 'NC'
        },
        {
          output: {
            code: 'LA10089-6',
            display: 'Room air',
            system: 'https://r.details.loinc.org/AnswerList/LL2746-2.html'
          },
          label: 'Room air',
          defaultInput: 'RA'
        },
        {
          output: {
            code: 'LA10087-0',
            display: 'Mask',
            system: 'https://r.details.loinc.org/AnswerList/LL2746-2.html'
          },
          label: 'Mask',
          defaultInput: 'MASK'
        }
      ],
      objectTemplate: {
        code: '',
        display: '',
        system: 'https://r.details.loinc.org/AnswerList/LL2746-2.html'
      }
    }
  },
  {
    // Read as a QuestionnaireResponse answer, not an Observation, so no
    // observation code dispatches it.
    name: 'cardiac-arrest',
    label: 'Cardiac Arrest (Compressions Status)',
    components: null,
    guidance: {
      resultType: 'string',
      description:
        'Maps the feed value to a compressions status. Read as the cardiac-arrest ' +
        'form\'s status answer; only "Initiated" is counted as an arrest.',
      inputHint:
        'Raw OBX-5 value exactly as your feed sends it — e.g. "Code Blue", ' +
        '"Initiated". Feeds already sending the canonical values need no rules: ' +
        'add a trailing wildcard "*" passthrough rule instead.',
      answers: [
        { output: 'Initiated', label: 'Compressions initiated', defaultInput: 'Code Blue' },
        { output: 'Stopped', label: 'Compressions stopped', defaultInput: 'Stopped' },
        { output: 'Ongoing', label: 'Compressions ongoing', defaultInput: 'Ongoing' },
        { output: 'Resumed', label: 'Compressions resumed', defaultInput: 'Resumed' }
      ]
    }
  },
  {
    name: 'lab-positivity',
    label: 'Lab Positivity (POS / NEG)',
    components: null,
    guidance: {
      resultType: 'string',
      description:
        'Maps the feed result string to a LOINC positive/negative answer code. ' +
        'Shared by the COVID / flu / RSV result cluster.',
      inputHint:
        'Raw OBX-5 result string exactly as your feed sends it — e.g. "POS", ' +
        '"Positive", "Detected", "NEG". Add one rule per variant your feed produces.',
      answers: [
        { output: 'LA6576-8', label: 'Positive', defaultInput: 'Positive' },
        { output: 'LA6577-6', label: 'Negative', defaultInput: 'Negative' }
      ]
    }
  },
  {
    name: 'disorientation',
    label: 'Disorientated',
    panelLoinc: '62476001',
    panelSystem: HL7_INSTRUMENT_CODE_SYSTEMS.SNOMED,
    components: null,
    guidance: {
      // The custom disorientation builder coerces both `{ value: "true" }`
      // objects and bare `"true"` strings (disorientation.ts
      // `extractBooleanFlag`). The seed and existing entries use the object
      // form, so it's canonical; the string form stays a valid alternate.
      resultType: 'object',
      acceptedResultTypes: ['string'],
      description:
        'Maps each feed value to "true" (disorientated) or "false" (not ' +
        'disorientated). The processor evaluates each ~-separated OBX-5 token ' +
        'separately and ORs the results.',
      inputHint:
        'One raw OBX-5 token per rule (e.g. "Oriented x3", "Confused", ' +
        '"Disoriented to time"). Tokens with no matching rule are ignored.',
      answers: [
        { output: { value: 'true' }, label: 'Disorientated' },
        { output: { value: 'false' }, label: 'Not disorientated' }
      ],
      objectTemplate: { value: '' }
    }
  },
  {
    name: 'ecart-oxygen-delivery-type',
    label: 'eCART Oxygen Delivery Type',
    panelSystem: HL7_INSTRUMENT_CODE_SYSTEMS.INTERNAL,
    components: null,
    guidance: {
      resultType: 'string',
      description:
        'Maps the feed device name to an AgileMD oxygen-delivery token, saved as ' +
        'the Observation `valueString` (internal code `ecart-oxygen-delivery-type`, ' +
        'consumed by eCART). Author rules MOST-ACUTE FIRST: when one OBX-5 carries ' +
        'several devices, the earliest matching rule wins (rule order = priority). ' +
        'Map your feed OBX-3 code to `ecart-oxygen-delivery-type` in clinical-codes; ' +
        'the priority-collapse is applied automatically.',
      inputHint:
        'Raw OBX-5 device string exactly as your feed sends it (e.g. "Vent", ' +
        '"Nasal Cannula", "Room Air"). Add one rule per string your feed produces — ' +
        'several strings (Vent / Ventilator / Mechanical Ventilator) can map to the ' +
        'same token. The answers below are listed in acuity (priority) order.',
      // Ordered most-acute → least-acute. The list is the priority order itself;
      // authored value-mapping rules should follow it top-to-bottom.
      answers: [
        { output: 'oscillator', label: 'Oscillator', defaultInput: 'Oscillator' },
        {
          output: 'mechanical-ventilator',
          label: 'Mechanical ventilator',
          defaultInput: 'Mechanical Ventilator'
        },
        { output: 'ambu-bag', label: 'Ambu bag', defaultInput: 'Ambu Bag' },
        {
          output: 'endotracheal-tube',
          label: 'Endotracheal tube',
          defaultInput: 'Endotracheal Tube'
        },
        {
          output: 'tracheostomy-tube',
          label: 'Tracheostomy tube',
          defaultInput: 'Tracheostomy Tube'
        },
        { output: 'laryngeal-mask', label: 'Laryngeal mask', defaultInput: 'Laryngeal Mask' },
        {
          output: 'supraglottic-airway',
          label: 'Supraglottic airway',
          defaultInput: 'Supraglottic Airway'
        },
        { output: 'bipap', label: 'BiPAP', defaultInput: 'BiPAP' },
        { output: 'cpap', label: 'CPAP', defaultInput: 'CPAP' },
        {
          output: 'transtracheal-catheter',
          label: 'Transtracheal catheter',
          defaultInput: 'Transtracheal Catheter'
        },
        { output: 't-piece', label: 'T-piece', defaultInput: 'T-Piece' },
        {
          output: 'high-flow-nasal-cannula',
          label: 'High flow nasal cannula',
          defaultInput: 'High Flow Nasal Cannula'
        },
        { output: 'oxytrach', label: 'Oxytrach', defaultInput: 'Oxytrach' },
        { output: 'oxytyke', label: 'Oxytyke', defaultInput: 'Oxytyke' },
        { output: 'oxymask', label: 'Oxymask', defaultInput: 'Oxymask' },
        {
          output: 'nonrebreather-mask',
          label: 'Non-rebreather mask',
          defaultInput: 'Non-Rebreather Mask'
        },
        {
          output: 'partial-rebreather-mask',
          label: 'Partial-rebreather mask',
          defaultInput: 'Partial-Rebreather Mask'
        },
        { output: 'venturi-mask', label: 'Venturi mask', defaultInput: 'Venturi Mask' },
        { output: 'oxygen-tent', label: 'Oxygen tent', defaultInput: 'Oxygen Tent' },
        { output: 'oxygen-hood', label: 'Oxygen hood', defaultInput: 'Oxygen Hood' },
        {
          output: 'tracheostomy-mask',
          label: 'Tracheostomy mask',
          defaultInput: 'Tracheostomy Mask'
        },
        { output: 'face-tent', label: 'Face tent', defaultInput: 'Face Tent' },
        {
          output: 'aerosol-oxygen-mask',
          label: 'Aerosol oxygen mask',
          defaultInput: 'Aerosol Oxygen Mask'
        },
        { output: 'simple-mask', label: 'Simple mask', defaultInput: 'Simple Mask' },
        { output: 'oxymizer', label: 'Oxymizer', defaultInput: 'Oxymizer' },
        { output: 'nasal-cannula', label: 'Nasal cannula', defaultInput: 'Nasal Cannula' },
        { output: 'blow-by', label: 'Blow-by', defaultInput: 'Blow-By' },
        { output: 'room-air', label: 'Room air', defaultInput: 'Room Air' }
      ]
    }
  },
  ...HL7_ADT_INSTRUMENTS
];

export const BLOOD_PRESSURE_PANEL_LOINC = '85354-9';

/**
 * LOINCs that name a multi-member observation panel (blood pressure, plus the
 * panel of every multi-component instrument above). A single OBX never maps to
 * one of these directly — a mapping names the member leaf and the processor
 * folds the panel from the leaves.
 */
export const HL7_PANEL_LOINCS: ReadonlySet<string> = new Set([
  BLOOD_PRESSURE_PANEL_LOINC,
  ...HL7_INSTRUMENT_REGISTRY.flatMap(i => (i.components && i.panelLoinc ? [i.panelLoinc] : []))
]);

const INSTRUMENTS_BY_NAME: ReadonlyMap<string, Hl7Instrument> = new Map(
  HL7_INSTRUMENT_REGISTRY.map(i => [i.name, i])
);

const COMPONENT_BY_NAME: ReadonlyMap<string, ReadonlyMap<string, Hl7InstrumentComponent>> = new Map(
  HL7_INSTRUMENT_REGISTRY.map(i => [i.name, new Map((i.components ?? []).map(c => [c.key, c]))])
);

export interface ParsedHl7LookupKey {
  instrument: Hl7Instrument;
  component: Hl7InstrumentComponent | null;
  /** Guidance for this exact lookup key (component-level when present). */
  guidance: InstrumentGuidance | null;
}

/**
 * Parse `hl7:<name>[:<component>]` into a registered instrument + component.
 * Returns null when the prefix is wrong, the instrument isn't registered,
 * or the component segment doesn't match a registered component.
 */
export function parseHl7LookupKey(lookupKey: string): ParsedHl7LookupKey | null {
  if (!lookupKey.startsWith('hl7:')) {
    return null;
  }

  const [, name, component] = lookupKey.split(':');

  if (!name) {
    return null;
  }

  const instrument = INSTRUMENTS_BY_NAME.get(name);

  if (!instrument) {
    return null;
  }

  if (!component) {
    if (instrument.components !== null) {
      // Required component missing.
      return null;
    }

    return { instrument, component: null, guidance: instrument.guidance ?? null };
  }

  const components = COMPONENT_BY_NAME.get(name);
  const matched = components?.get(component) ?? null;

  if (!matched) {
    return null;
  }

  return { instrument, component: matched, guidance: matched.guidance };
}

export function buildHl7LookupKey(instrumentName: string, componentKey?: string): string {
  return componentKey ? `hl7:${instrumentName}:${componentKey}` : `hl7:${instrumentName}`;
}

/**
 * Observation code → the `hl7:*` lookup key whose `value-mappings` entry
 * normalizes that observation's OBX-5. Lives here rather than in hl7-processor
 * so the admin console can pair a `clinical-codes` row with the
 * `value-mappings` row that feeds it (and back again); the processor's runtime
 * dispatch derives its refs from this map, so the association has one source.
 *
 * Codes are either a `GLOBAL_OBSERVATION_REGISTRY` code (matched directly on
 * OBX-3) or a resolved-only internal code reached via a clinical-codes remap.
 * Instruments driven by a `customBuilder` rather than a value mapping
 * (Disorientated) are absent, as are instruments with no observation at all
 * (`HL7_ADT_INSTRUMENTS` read ADT fields, and
 * lab-positivity is attached per-org rather than by code).
 */
export const HL7_OBSERVATION_VALUE_MAPPING_KEYS: Readonly<Record<string, string>> = {
  '281400004': 'hl7:ramsay',
  '3152-6': 'hl7:oxygen-device',
  '38222-6': 'hl7:braden:sensory',
  '38223-4': 'hl7:braden:activity',
  '38224-2': 'hl7:braden:mobility',
  '38225-9': 'hl7:braden:nutrition',
  '38226-7': 'hl7:braden:friction',
  '38227-5': 'hl7:braden:total',
  '38229-1': 'hl7:braden:moisture',
  '457441000124102': 'hl7:rass',
  '67775-7': 'hl7:nemsis-lor',
  '70184-7': 'hl7:nih-loc',
  '9267-6': 'hl7:glasgow:eye',
  '9268-4': 'hl7:glasgow:motor',
  '9269-2': 'hl7:glasgow:total',
  '9270-0': 'hl7:glasgow:verbal',
  'ecart-oxygen-delivery-type': 'hl7:ecart-oxygen-delivery-type'
};

/**
 * Inclusive numeric span a passthrough range rule should cover for this
 * guidance, or `null` when it has none. Enumerated numeric answers (RASS,
 * Ramsay) give the span; a continuous total states it in `scoreRange`. A
 * single-value span is `null` because the rule schema requires `min < max`.
 */
export function numericScoreSpan(
  guidance: InstrumentGuidance | null | undefined
): { min: number; max: number } | null {
  if (!guidance || guidance.resultType !== 'number') {
    return null;
  }

  const answers = guidance.answers ?? [];
  const scores = answers.map(a => a.output).filter((o): o is number => typeof o === 'number');
  const span =
    scores.length === answers.length && scores.length > 0
      ? { min: Math.min(...scores), max: Math.max(...scores) }
      : guidance.scoreRange;

  return span && span.min < span.max ? { min: span.min, max: span.max } : null;
}

/** The observation codes `lookupKey` normalizes, for the reverse lookup. */
export function observationCodesForLookupKey(lookupKey: string): string[] {
  return Object.entries(HL7_OBSERVATION_VALUE_MAPPING_KEYS)
    .filter(([, key]) => key === lookupKey)
    .map(([code]) => code);
}
